import PropTypes from 'prop-types';
import React, { createContext, useContext, useState, useRef, useCallback, useMemo } from 'react';

const DisputeContext = createContext();

export const useDisputeContext = () => {
	const context = useContext(DisputeContext);
	if (!context) {
		throw new Error('useDisputeContext must be used within a DisputeProvider');
	}
	return context;
};

export const DisputeProvider = ({ children }) => {
	const [openSection, setOpenSection] = useState(null);
	const [openDisputeReason, setOpenDisputeReason] = useState(null);
	const cardRefs = useRef({});

	const handleOpenSection = useCallback((section) => {
		setOpenSection(prevSection => section === prevSection ? null : section);
		setOpenDisputeReason(null);
	}, []);

	const handleOpenDisputeReason = useCallback((_disputeId) => {
		const isOpening = _disputeId !== openDisputeReason;
		setOpenDisputeReason(prevId => _disputeId === prevId ? null : _disputeId);

		if (isOpening && _disputeId) {
			setTimeout(() => {
				const cardRef = cardRefs.current[_disputeId];
				if (cardRef) {
					const cardRect = cardRef.getBoundingClientRect();

					const absoluteCardTop = cardRect.top + window.scrollY;

					const padding = 120;

					window.scrollTo({
						top: absoluteCardTop - padding,
						behavior: 'smooth'
					});
				}
			}, 300);
		}
	}, [openDisputeReason]);

	const closeDisputeReason = useCallback(() => {
		setOpenDisputeReason(null);
	}, []);

	const closeSection = useCallback(() => {
		setOpenSection(null);
	}, []);

	const registerCardRef = useCallback((id, ref) => {
		cardRefs.current[id] = ref;
	}, []);

	const contextValue = useMemo(() => ({
		openSection,
		openDisputeReason,
		handleOpenSection,
		handleOpenDisputeReason,
		closeDisputeReason,
		closeSection,
		registerCardRef,
	}), [
		openSection,
		openDisputeReason,
		handleOpenSection,
		handleOpenDisputeReason,
		closeDisputeReason,
		closeSection,
		registerCardRef,
	]);

	return (
		<DisputeContext.Provider value={contextValue}>
			{children}
		</DisputeContext.Provider>
	);
};

DisputeProvider.propTypes = {
	children: PropTypes.node.isRequired,
};
